import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { GameType, PhaserGameType, PhaserHttpMethod, PhaserToAngularEvent } from 'angular-to-phaser';
import type { IGameService } from 'crittergames';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MultiplayerGameClient } from 'src/app/components/games/multiplayergameclient';
import { environment } from 'src/environments/environment';
import { GameConfig, GameScoreResult, GameStateResponse, Interaction, LeaderboardEntryDTO, MultiplayerGameSummary } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { InventoryService } from './inventory.service';
import { PetService } from './pet.service';
import { getSessionName, withSessionName } from './telemetry/session-name';

@Injectable({
    providedIn: 'root'
})
export class GameDataService extends AbstractService implements IGameService {

    /*Signing Key for score jwts */
    private activeGameKey = 'neverauthed';

    /** Trunk connection for all active multiplayer games that this client is playing */
    private connection: signalR.HubConnection;
    /** GameID keys used to handle the RPC dispatch to the two maps (connectedGamesAnd*) */
    private connectedGames: string[] = [];

    /** List of all clients, such as HighlowComponent, which are used to receive standard SignalR RPCs, keyed by GameId */
    private connectedGamesAndClients: Map<string, MultiplayerGameClient> = new Map();
    /** List of all clients' custom RPCs that are not part of the Critters SignalR Multiplayer game protocol (see HighlowComponent.ReceiveGameStateUpdate)   */
    private connectedGamesAndRPCHandlers: Map<string, (...any) => void> = new Map();
    private allCustomHandlers: string[] = [];

    /** Handlers for phaser events */
    private phaserEventHandlers: ((evt: PhaserToAngularEvent, ctx: any) => void)[] = [];

    /** Fires whenever a score submission completes, so any open leaderboard UI can refetch. */
    public scoreSubmitted$ = new Subject<GameScoreResult>();

    constructor(private http: HttpClient, private coreAppService: ApplicationService, private petService: PetService, private inventoryService: InventoryService, private ngZone: NgZone) {
        super(coreAppService.authService, coreAppService.featureFlagService);
    }

    public retrieveGameConfigs(): Observable<GameConfig[]> {
        return this.http.get<GameConfig[]>(`${environment.apiUrl}/game/`, this.httpOptionsAuthJson())
            .pipe(
                map(gcfgs =>
                    gcfgs.map((gcfg: GameConfig) => new GameConfig(gcfg))));
    }

    public retrieveGameConfig(gameType: GameType): Observable<GameConfig> {
        return this.http.get<GameConfig[]>(`${environment.apiUrl}/game?id=${<number>gameType}`, this.httpOptionsAuthJson())
            .pipe(map(gcfgs => new GameConfig(gcfgs[0])));
    }

    public retrieveMultiplayerGameLobbyIds(gameType: GameType): Observable<MultiplayerGameSummary[]> {
        return this.http.get<MultiplayerGameSummary[]>(`${environment.apiUrl}/game/list/${<number>gameType}`, this.httpOptionsAuthJson());
    }

    public submitGameCommand(gameId: string, command: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/game/command/${gameId}`, { Command: JSON.stringify(command) }, this.httpOptionsAuthJson());
    }

    public retrieveSnowballBattleReport(): Observable<{ Report: string }> {
        return this.http.get<{ Report: string }>(`${environment.apiUrl}/games/snowballFight/battleReport`, this.httpOptionsAuthJson());
    }

    /****** SIGNALR METHODS ******/
    /** These methods represent SignalR RPC calls and client connection management methods - however because we don't want every game to have its own HTTP connection, we run a
     * sort of dispatch through this service. Each game has its own sendChat() logic and receiveChat() logic, but its all mediated here, dispatched via the gameId
     */
    private async connectToSignalR() {
        if (this.connectionIsLiving()) { return; }
        else if (this.connectionIsDead()) {
            const sessionName = await getSessionName();
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(withSessionName(environment.signalRBase + '/gamehub', sessionName), { accessTokenFactory: () => this.authService.jwtToken })
                .configureLogging(signalR.LogLevel.Information)
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: ctx => {
                        if (ctx.previousRetryCount < 5) { //this will retry 5 times over the course of 3.75sec
                            return ctx.previousRetryCount * 250;
                        }
                        if (ctx.previousRetryCount < 30) { //this will retry 25 times over 6 minutes
                            return 15000;
                        }
                        return null; //if you don't reconnect after 6 minutes, your shit is ruined, too bad.
                    }
                })
                .build();
            this.connection.on('ReceiveGameOver', this.receiveGameOver.bind(this));
            this.connection.on('ReceiveSystemMessage', this.receiveSystemMessage.bind(this));
            this.connection.on('ReceiveChat', this.receiveChat.bind(this));

            this.connection.onclose(() => {
                this.connectedGamesAndClients.forEach(c => c.receiveSuddenDisconnect());
            });


            return this.startSignalR();
        }
        console.error(`Zombie connection was in unexpected state: ${this.connection.state}`);
    }

    private connectionIsLiving(): boolean {
        return this.connection && (this.connection.state === signalR.HubConnectionState.Connected ||
            this.connection.state === signalR.HubConnectionState.Connecting ||
            this.connection.state === signalR.HubConnectionState.Reconnecting);
    }

    private connectionIsDead(): boolean {
        return (!this.connection || (this.connection.state === signalR.HubConnectionState.Disconnected) || (this.connection.state === signalR.HubConnectionState.Disconnecting));
    }

    private disconnectSignalR() {
        console.warn('Disconnecting signalR');

        if (this.connection) {
            this.connection.off('ReceiveGameOver');
            this.connection.off('ReceiveSystemMessage');
            this.connection.off('ReceiveChat');
            this.allCustomHandlers.forEach(h => this.connection.off(h));

            this.connection.stop().catch(e => {
                console.warn(e);
            });
        }
        this.connectedGamesAndClients.forEach(c => c.receiveGameOver(null));
        this.connectedGamesAndClients.clear();
        this.connectedGamesAndRPCHandlers.clear();
    }

    public disconnectGameClient(gameId: string) {
        this.connectedGamesAndClients.delete(gameId);
        const removals: string[] = [];
        this.connectedGamesAndRPCHandlers.forEach((v, k) => {
            if (k.includes(gameId)) {
                removals.push(k);
            }
        });
        removals.forEach(r => this.connectedGamesAndRPCHandlers.delete(r));

        this.connectedGames.splice(this.connectedGames.findIndex(gID => gID === gameId), 1);

        if (this.connectedGamesAndClients.size === 0) {
            this.disconnectSignalR();
        }
    }

    public async connectSignalRToGame(gameId: string, client: MultiplayerGameClient): Promise<void> { //todo make these SigR methods accessible to phaser
        if (!this.connectedGames.find(connectedGameId => gameId === connectedGameId) || this.connectionIsDead()) { //if cant find it or dead connection, connect
            return this.connectToSignalR().then(() => {
                this.connection.invoke('Connect', gameId).then(() => {
                    if (!this.connectedGames.find(g => g === gameId)) {
                        this.connectedGames.push(gameId);
                    }
                    this.connectedGamesAndClients.set(gameId, client);
                });
            });
        }
        return Promise.reject();
    }

    public sendChat(gameId: string, message: string) {
        this.connectToSignalR().then(() => {
            this.connection.invoke('SendChatMessage', gameId, message);
        });
    }

    public sendGameInput(gameId: string, input: any): Promise<void> {
        return this.connectToSignalR().then(() => {
            this.connection.invoke('AcceptGameInput', gameId, JSON.stringify(input));
        });
    }

    private receiveChat(gameId: string, sender: string, message: string) {
        this.connectedGamesAndClients.get(gameId).receiveChat(gameId, sender, message);
    }

    private receiveSystemMessage(gameId: string, message: string) {
        this.connectedGamesAndClients.get(gameId).receiveSystemMessage(gameId, message);
    }

    private receiveGameOver(gameId: string) {
        this.connectedGamesAndClients.get(gameId).receiveGameOver(gameId);
        this.disconnectGameClient(gameId);
    }

    /**
     * This is not, conceptually, different from the behavior of receiveChat/receiveSystemMessage/receiveGameOver
     * The difference is that those are well defined methods common to every multiplayer game, but other games may define other
     * remote procedure calls to their clients. This allows those clients to register their own listener methods, and
     * this service to appropriately dispatch to them.
     * As an example, follow HighLow's ReceiveGameStateUpdate logic e2e
     *
     * @param gameId
     * @param handle
     * @param fn
     */
    public addSignalRHandler(gameId: string, handle: string, fn: (...any) => void) {
        if (this.connectedGamesAndRPCHandlers.get(gameId + handle)) { //remove old handlers if this is a reconnect
            this.connection.off(gameId + handle, this.connectedGamesAndRPCHandlers.get(gameId + handle));
        }
        //add new handler
        this.connectedGamesAndRPCHandlers.set(gameId + handle, fn);
        this.allCustomHandlers.push(handle);

        //invoke handler when the time is right
        this.connection.on(handle, (...any) => {
            const fn = this.connectedGamesAndRPCHandlers.get(gameId + handle);
            if (fn) { fn(...any); }
        });
    }

    public invokeSignalR(methodName: string, ...args) {
        this.connectToSignalR().then(() => {
            this.connection.invoke(methodName, args);
        });
    }

    private async startSignalR() {
        try {
            await this.connection.start();
            console.log('SignalR Connected.');
        } catch (err) {
            console.log(err);
            setTimeout(() => this.startSignalR(), 1000);
        }
    };
    /****** SIGNALR METHODS ******/


    /****** PHASER INTERNAL METHODS ******/
    public getToken(gameType: GameType | PhaserGameType): void {
        this.retrieveSigningKey(gameType).subscribe(key => this.activeGameKey = key.Key);
    }

    public submitScore(gameType: GameType | PhaserGameType, score: number, doneCallback: (result: GameScoreResult) => void, errorCallback: (error: any) => void) {
        let now = Math.floor(Date.now() / 1000);
        const scoreToken = { g: gameType, u: this.authService.authenticatedUser$.getValue().Username, s: score, exp: now + 6000, iat: now, nbf: now, iss: environment.tokenIss };

        this.generateSignedToken(scoreToken).then(signedToken => this.sendScore(gameType, score, signedToken)
            .pipe(catchError(error => { errorCallback(error); throw error; }))
            .subscribe(r => {
                // Called from outside Angular's zone (score submission is Phaser-initiated) - re-enter
                // so the cash update reaches the header's change detection.
                this.ngZone.run(() => {
                    this.coreAppService.userService.updateUserCash(r.CashWon);
                    doneCallback(r);
                    this.scoreSubmitted$.next(r);
                });
            }));
    }

    public retrieveLeaderboard(gameType: GameType | PhaserGameType): Observable<LeaderboardEntryDTO[]> {
        return this.http.get<LeaderboardEntryDTO[]>(`${environment.apiUrl}/game/leaderboard/${<number>gameType}`, this.httpOptionsAuthJson());
    }

    public isAuthenticated(): boolean {
        return this.authService.isAuthenticated() && this.activeGameKey !== 'neverauthed';
    }

    public reportAngularEvent(event: PhaserToAngularEvent, ctx: any) {
        // Phaser calls this from outside Angular's zone (see PhaserComponent.ngOnInit) - re-enter
        // so handlers that touch Angular-bound state (e.g. opening a modal) actually get picked up
        // by change detection.
        this.ngZone.run(() => this.phaserEventHandlers.forEach(handler => handler(event, ctx)));
    }

    public executeApiCall<T>(method: PhaserHttpMethod, path: string, signRequest: boolean, gameType: GameType | PhaserGameType, body: any): Promise<T> {
        if (signRequest && !this.activeGameKey) {
            return firstValueFrom(this.retrieveSigningKey(gameType)
                .pipe(
                    switchMap(key => {
                        this.activeGameKey = key.Key;
                        return this.executeApiCall<T>(method, path, signRequest, gameType, body)
                    })));
        }
        let apiCallSupplierFunc: (s) => Observable<T>;

        switch (method) {
            case PhaserHttpMethod.POST: apiCallSupplierFunc = (signingToken) => {
                if (body) body.SigningToken = signingToken;
                return this.http.post<T>(`${environment.apiUrl}${path}${!body && signingToken ? '/' + signingToken : ''}`, body, this.httpOptionsAuthJson());
            }; break;
            case PhaserHttpMethod.PUT: apiCallSupplierFunc = (signingToken) => {
                if (body) body.SigningToken = signingToken;
                return this.http.put<T>(`${environment.apiUrl}${path}${!body && signingToken ? '/' + signingToken : ''}`, body, this.httpOptionsAuthJson());
            }; break;
            case PhaserHttpMethod.PATCH: apiCallSupplierFunc = (signingToken) => {
                if (body) body.SigningToken = signingToken;
                return this.http.patch<T>(`${environment.apiUrl}${path}${!body && signingToken ? '/' + signingToken : ''}`, body, this.httpOptionsAuthJson());
            }; break;
            case PhaserHttpMethod.DELETE: apiCallSupplierFunc = (signingToken) => this.http.delete<T>(`${environment.apiUrl}${path}${signingToken ? '/' + signingToken : ''}`, this.httpOptionsAuthJson()); break;
            case PhaserHttpMethod.GET: apiCallSupplierFunc = (signingToken) => this.http.get<T>(`${environment.apiUrl}${path}${signingToken ? '/' + signingToken : ''}`, this.httpOptionsAuthJson()); break;
        }
        if (signRequest) {
            let now = Math.floor(Date.now() / 1000);

            var scoreToken = { g: gameType, u: this.authService.authenticatedUser$.getValue().Username, exp: now + 6000, iat: now, nbf: now, iss: environment.tokenIss }
            return this.generateSignedToken(scoreToken).then(signedToken => firstValueFrom(apiCallSupplierFunc(signedToken)));
        } else {
            return firstValueFrom(apiCallSupplierFunc(null));
        }
    }

    private handleAnonymousResponseForService(response: any) {
        if (response) {
            if (response.InteractionType != null && response.InteractionType != null) {
                response = new Interaction(response, this.coreAppService.jsonMapper.jsonEnricher());
                GameStateResponse.distributeGameStateToServices(response, this.inventoryService, this.coreAppService.userService, this.petService);
            }
        }
        return response;
    }

    private generateSignedToken(securePayload: object | string | any): Promise<string> {
        const stringPayload = JSON.stringify(securePayload);
        const header = {
            alg: 'HS256',
            typ: 'JWT'
        };
        const headStr = JSON.stringify(header);
        const urlSafeToken = `${btoa(headStr).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+/g, '')}.${btoa(stringPayload).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+/g, '')}`;
        return this.getUrlSafeSignatureForToken(this.activeGameKey, urlSafeToken).then(signature => `${urlSafeToken}.${signature}`);
    }

    private async getUrlSafeSignatureForToken(key: string, unsignedToken: string) {
        const enc = new TextEncoder();
        const keyBytes = Uint8Array.from(atob(key), c => c.charCodeAt(0));

        const signingKey = await crypto.subtle.importKey('raw', keyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);

        const rawSignature = await crypto.subtle.sign('HMAC', signingKey, enc.encode(unsignedToken));
        const arr = new Uint8Array(rawSignature);
        let bin = '';
        arr.forEach(c => bin += String.fromCharCode(c));

        return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+/g, '');
    }

    /**
     * HTTP helper method to get user's unique game signing key
     */
    private retrieveSigningKey(gameType: GameType | PhaserGameType): Observable<{ Key: string }> {
        return this.http.get<{ Key: string }>(`${environment.apiUrl}/game/scoreAuth/${gameType}`, this.httpOptionsAuthJson());
    }

    /**
     * HTTP helper method to submit user's score
     */
    private sendScore(gameType: GameType | PhaserGameType, score: number, token: string): Observable<GameScoreResult> {
        return this.http.post<GameScoreResult>(`${environment.apiUrl}/game/gameScore/${gameType}/${score}/${token}`, null, this.httpOptionsAuthJson());
    }
    /****** PHASER INTERNAL METHODS ******/
    public addPhaserHandler(handler: (evt: PhaserToAngularEvent, ctx: any) => void) {
        this.phaserEventHandlers.push(handler);
    }

    public removePhaserHandler(handler: (evt: PhaserToAngularEvent, ctx: any) => void) {
        const idx = this.phaserEventHandlers.findIndex(h => h === handler);
        if (idx >= 0) {
            this.phaserEventHandlers.splice(idx, 1);
        }
    }
    /******** ANGULAR HOOKS FOR PHASER *********/

}
